import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import AuthGuard from "./auth/components/AuthGuard";
import Unauthorized from "./auth/pages/Unauthorized";
import { withRouteProtection } from "./auth/utils/routeHelpers";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Login from "./modules/auth/Login";
import Dashboard from "./modules/dashboard/Dashboard";
import hrRoutes from "./modules/hr/hrRoutes";
import AppLayout from "./components/layout/AppLayout";
import { CompanyDetails } from "./modules/company";
import CompanySettings from "./modules/companySettings/pages/CompanySettings";
import stageRoutes from "./modules/stage/stageRoutes";
import clientRoutes from "./modules/client/clientRoutes";
import inquiryRoutes from "./modules/sales/inquiry/inquiryRoutes";
import RootRedirect from "./components/common/RootRedirect";
import SalesReport from "./modules/reports/pages/SalesReport";
import TimeLagReport from "./modules/timelag/pages/TimeLagReport";
import BulkTransfer from "./modules/bulkTransfer/pages/BulkTransfer";
import paymentTermsRoutes from "./modules/paymentTerms/paymentTermsRoutes";
import NotAttended30DaysReport from "./modules/reports/pages/NotAttended30DaysReport";
import contactRoutes from "./modules/contact/contactRoutes";
import followupRoutes from "./modules/sales/followup/followupRoutes";
import ContactDirectoryReport from "./modules/reports/pages/ContactDirectoryReport";
import ContactDirectoryReportAssignedTo from "./modules/reports/pages/ContactDirectoryReportAssignedTo";
import ContactDirectoryReportCreatedBy from "./modules/reports/pages/ContactDirectoryReportCreatedBy";

const App: React.FC = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* Authenticated routes with layout */}
          <Route
            element={
              <AuthGuard>
                <AppLayout />
              </AuthGuard>
            }
          >
            {stageRoutes.map((route, index) => (
              <Route
                key={`stage-${index}`}
                path={route.path}
                element={withRouteProtection(route.path, route.element)}
              />
            ))}

            {clientRoutes.map((route, index) => (
              <Route
                key={`client-${index}`}
                path={route.path}
                element={withRouteProtection(route.path, route.element)}
              />
            ))}

            {contactRoutes.map((route, index) => (
              <Route
                key={`contact-${index}`}
                path={route.path}
                element={withRouteProtection(route.path, route.element)}
              />
            ))}

            {inquiryRoutes.map((route, index) => (
              <Route
                key={`inquiry-${index}`}
                path={route.path}
                element={withRouteProtection(route.path, route.element)}
              />
            ))}

            {followupRoutes.map((route, index) => (
              <Route
                key={`followup-${index}`}
                path={route.path}
                element={withRouteProtection(route.path, route.element)}
              />
            ))}

            <Route
              path="/dashboard"
              element={withRouteProtection("/dashboard", <Dashboard />)}
            />

            {hrRoutes.map((route, index) => (
              <Route
                key={`hr-${index}`}
                path={route.path}
                element={withRouteProtection(route.path, route.element)}
              />
            ))}

            <Route
              path="/company"
              element={withRouteProtection("/company", <CompanyDetails />)}
            />
            <Route
              path="/company/settings"
              element={withRouteProtection("/company/settings", <CompanySettings />)}
            />
            <Route
              path="/:vertical/reports"
              element={withRouteProtection("/:vertical/reports", <SalesReport />)}
            />
            <Route
              path="/reports/contact-directory"
              element={withRouteProtection(
                "/reports/contact-directory",
                <ContactDirectoryReport />
              )}
            />
            <Route
              path="/reports/contact-directory-assigned-to"
              element={withRouteProtection(
                "/reports/contact-directory-assigned-to",
                <ContactDirectoryReportAssignedTo />
              )}
            />
            <Route
              path="/reports/contact-directory-created-by"
              element={withRouteProtection(
                "/reports/contact-directory-created-by",
                <ContactDirectoryReportCreatedBy />
              )}
            />
            <Route
              path="/:vertical/reports/not-attended-30-days"
              element={withRouteProtection(
                "/:vertical/reports/not-attended-30-days",
                <NotAttended30DaysReport />
              )}
            />
            <Route
              path="/timelag-report"
              element={withRouteProtection("/timelag-report", <TimeLagReport />)}
            />
            <Route
              path="/bulk-transfer"
              element={withRouteProtection("/bulk-transfer", <BulkTransfer />)}
            />

            {paymentTermsRoutes.map((route, index) => {
              const path = route.path ?? "/payment-terms";
              return (
                <Route
                  key={`payment-${index}`}
                  path={path}
                  element={withRouteProtection(path, route.element)}
                />
              );
            })}
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme="colored"
      />
    </AuthProvider>
  );
};

export default App;
